import React from 'react';

// Composant WorkflowSection déplacé à l'extérieur pour éviter les re-renders
const WorkflowSection = React.memo(({ type, title, bgColor, data: workflowData, updateWorkflow, addEtape, updateEtape, removeEtape }) => (
  <div className={`border border-gray-200 rounded-lg p-6 ${bgColor}`}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold">{title}</h3>
      <label className="flex items-center">
        <input
          type="checkbox"
          checked={workflowData?.actif || false}
          onChange={(e) => updateWorkflow(type, 'actif', e.target.checked)}
          className="mr-2 h-4 w-4 text-blue-600 rounded"
        />
        <span className="text-sm font-medium">Actif</span>
      </label>
    </div>

    {/* Description du workflow */}
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Description du parcours patient
      </label>
      <textarea
        value={workflowData?.description || ''}
        onChange={(e) => updateWorkflow(type, 'description', e.target.value)}
        rows="4"
        placeholder="Nom, version, fonctionnement du parcours, type d'inclusions, spécificités (rappels de rendez-vous...)"
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>

    {/* Étapes du workflow */}
    <div>
      <div className="flex justify-between items-center mb-3">
        <h4 className="font-medium text-gray-700">Étapes du parcours</h4>
        <button
          onClick={() => addEtape(type)}
          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
        >
          + Ajouter une étape
        </button>
      </div>

      {workflowData?.etapes && workflowData.etapes.length > 0 ? (
        <div className="space-y-3">
          {workflowData.etapes.map((etape, index) => (
            <div key={etape.id} className="border border-gray-200 rounded p-4 bg-white">
              <div className="flex justify-between items-start mb-3">
                <h5 className="font-medium">Étape {etape.ordre}</h5>
                <button
                  onClick={() => removeEtape(type, index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Nom de l'étape</label>
                  <input
                    type="text"
                    value={etape.nom || ''}
                    onChange={(e) => updateEtape(type, index, 'nom', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Responsable</label>
                  <input
                    type="text"
                    value={etape.responsable || ''}
                    onChange={(e) => updateEtape(type, index, 'responsable', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Délai</label>
                  <input
                    type="text"
                    value={etape.delai || ''}
                    onChange={(e) => updateEtape(type, index, 'delai', e.target.value)}
                    placeholder="Ex: J-7, H+24"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Actions</label>
                  <input
                    type="text"
                    value={etape.actions || ''}
                    onChange={(e) => updateEtape(type, index, 'actions', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-600 mb-1">Description</label>
                  <textarea
                    value={etape.description || ''}
                    onChange={(e) => updateEtape(type, index, 'description', e.target.value)}
                    rows="2"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded">
          Aucune étape définie
        </p>
      )}
    </div>
  </div>
));

export const WorkflowTab = ({ data, setData }) => {
  const updateWorkflow = React.useCallback((type, field, value) => {
    setData(prev => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        [type]: {
          ...prev.workflow?.[type],
          [field]: value
        }
      }
    }));
  }, [setData]);

  const addEtape = React.useCallback((type) => {
    setData(prev => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        [type]: {
          ...prev.workflow?.[type],
          etapes: [...(prev.workflow?.[type]?.etapes || []), {
            id: Date.now(),
            ordre: (prev.workflow?.[type]?.etapes?.length || 0) + 1,
            nom: '',
            description: '',
            responsable: '',
            delai: '',
            actions: ''
          }]
        }
      }
    }));
  }, [setData]);

  const updateEtape = React.useCallback((type, index, field, value) => {
    setData(prev => {
      const updatedEtapes = [...(prev.workflow?.[type]?.etapes || [])];
      updatedEtapes[index] = {
        ...updatedEtapes[index],
        [field]: value
      };
      return {
        ...prev,
        workflow: {
          ...prev.workflow,
          [type]: {
            ...prev.workflow?.[type],
            etapes: updatedEtapes
          }
        }
      };
    });
  }, [setData]);

  const removeEtape = React.useCallback((type, index) => {
    setData(prev => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        [type]: {
          ...prev.workflow?.[type],
          etapes: (prev.workflow?.[type]?.etapes || []).filter((_, i) => i !== index)
        }
      }
    }));
  }, [setData]);

  const addAutreWorkflow = React.useCallback(() => {
    setData(prev => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        autres: [...(prev.workflow?.autres || []), {
          id: Date.now(),
          nom: '',
          actif: false,
          description: '',
          etapes: []
        }]
      }
    }));
  }, [setData]);

  const removeAutreWorkflow = React.useCallback((index) => {
    setData(prev => ({
      ...prev,
      workflow: {
        ...prev.workflow,
        autres: (prev.workflow?.autres || []).filter((_, i) => i !== index)
      }
    }));
  }, [setData]);

  // Fonction spécifique pour les workflows "autres" avec gestion d'index
  const updateAutreWorkflow = React.useCallback((index, field, value) => {
    setData(prev => {
      const updatedAutres = [...(prev.workflow?.autres || [])];
      updatedAutres[index] = {
        ...updatedAutres[index],
        [field]: value
      };
      return {
        ...prev,
        workflow: {
          ...prev.workflow,
          autres: updatedAutres
        }
      };
    });
  }, [setData]);

  const addEtapeAutre = React.useCallback((index) => {
    setData(prev => {
      const updatedAutres = [...(prev.workflow?.autres || [])];
      updatedAutres[index] = {
        ...updatedAutres[index],
        etapes: [...(updatedAutres[index]?.etapes || []), {
          id: Date.now(),
          ordre: (updatedAutres[index]?.etapes?.length || 0) + 1,
          nom: '',
          description: '',
          responsable: '',
          delai: '',
          actions: ''
        }]
      };
      return {
        ...prev,
        workflow: {
          ...prev.workflow,
          autres: updatedAutres
        }
      };
    });
  }, [setData]);

  const updateEtapeAutre = React.useCallback((workflowIndex, etapeIndex, field, value) => {
    setData(prev => {
      const updatedAutres = [...(prev.workflow?.autres || [])];
      const updatedEtapes = [...(updatedAutres[workflowIndex]?.etapes || [])];
      updatedEtapes[etapeIndex] = {
        ...updatedEtapes[etapeIndex],
        [field]: value
      };
      updatedAutres[workflowIndex] = {
        ...updatedAutres[workflowIndex],
        etapes: updatedEtapes
      };
      return {
        ...prev,
        workflow: {
          ...prev.workflow,
          autres: updatedAutres
        }
      };
    });
  }, [setData]);

  const removeEtapeAutre = React.useCallback((workflowIndex, etapeIndex) => {
    setData(prev => {
      const updatedAutres = [...(prev.workflow?.autres || [])];
      updatedAutres[workflowIndex] = {
        ...updatedAutres[workflowIndex],
        etapes: (updatedAutres[workflowIndex]?.etapes || []).filter((_, i) => i !== etapeIndex)
      };
      return {
        ...prev,
        workflow: {
          ...prev.workflow,
          autres: updatedAutres
        }
      };
    });
  }, [setData]);

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Workflows / Usage Métier</h2>

      {/* Workflow Suivi Connecté */}
      <WorkflowSection 
        type="suiviConnecte"
        title="Workflow Suivi Connecté"
        bgColor="bg-blue-50"
        data={data.suiviConnecte || {}}
        updateWorkflow={updateWorkflow}
        addEtape={addEtape}
        updateEtape={updateEtape}
        removeEtape={removeEtape}
      />

      {/* Workflow Dossier Administratif / Préadmission */}
      <WorkflowSection 
        type="dossierAdministratif"
        title="Workflow Dossier Administratif / Préadmission"
        bgColor="bg-green-50"
        data={data.dossierAdministratif || {}}
        updateWorkflow={updateWorkflow}
        addEtape={addEtape}
        updateEtape={updateEtape}
        removeEtape={removeEtape}
      />

      {/* Autres workflows - avec composant spécialisé */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-purple-700">Autres Workflows</h3>
          <button
            onClick={addAutreWorkflow}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            + Ajouter un workflow
          </button>
        </div>

        {data.autres && data.autres.length > 0 ? (
          <div className="space-y-4">
            {data.autres.map((workflow, index) => (
              <div key={workflow.id} className="border border-purple-200 rounded-lg p-6 bg-purple-50">
                <div className="flex justify-between items-start mb-4">
                  <input
                    type="text"
                    value={workflow.nom || ''}
                    onChange={(e) => updateAutreWorkflow(index, 'nom', e.target.value)}
                    placeholder="Nom du workflow"
                    className="text-lg font-semibold bg-transparent border-b-2 border-purple-300 focus:outline-none focus:border-purple-500"
                  />
                  <button
                    onClick={() => removeAutreWorkflow(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Contenu du workflow personnalisé */}
                <div className="border border-purple-200 rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={workflow.actif || false}
                        onChange={(e) => updateAutreWorkflow(index, 'actif', e.target.checked)}
                        className="mr-2 h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="text-sm font-medium">Actif</span>
                    </label>
                  </div>

                  {/* Description du workflow */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description du parcours patient
                    </label>
                    <textarea
                      value={workflow.description || ''}
                      onChange={(e) => updateAutreWorkflow(index, 'description', e.target.value)}
                      rows="4"
                      placeholder="Nom, version, fonctionnement du parcours, type d'inclusions, spécificités..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Étapes du workflow */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-gray-700">Étapes du parcours</h4>
                      <button
                        onClick={() => addEtapeAutre(index)}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                      >
                        + Ajouter une étape
                      </button>
                    </div>

                    {workflow.etapes && workflow.etapes.length > 0 ? (
                      <div className="space-y-3">
                        {workflow.etapes.map((etape, etapeIndex) => (
                          <div key={etape.id} className="border border-gray-200 rounded p-4 bg-gray-50">
                            <div className="flex justify-between items-start mb-3">
                              <h5 className="font-medium">Étape {etape.ordre}</h5>
                              <button
                                onClick={() => removeEtapeAutre(index, etapeIndex)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Nom de l'étape</label>
                                <input
                                  type="text"
                                  value={etape.nom || ''}
                                  onChange={(e) => updateEtapeAutre(index, etapeIndex, 'nom', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Responsable</label>
                                <input
                                  type="text"
                                  value={etape.responsable || ''}
                                  onChange={(e) => updateEtapeAutre(index, etapeIndex, 'responsable', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Délai</label>
                                <input
                                  type="text"
                                  value={etape.delai || ''}
                                  onChange={(e) => updateEtapeAutre(index, etapeIndex, 'delai', e.target.value)}
                                  placeholder="Ex: J-7, H+24"
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Actions</label>
                                <input
                                  type="text"
                                  value={etape.actions || ''}
                                  onChange={(e) => updateEtapeAutre(index, etapeIndex, 'actions', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-xs text-gray-600 mb-1">Description</label>
                                <textarea
                                  value={etape.description || ''}
                                  onChange={(e) => updateEtapeAutre(index, etapeIndex, 'description', e.target.value)}
                                  rows="2"
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded">
                        Aucune étape définie
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-500 mb-4">Aucun autre workflow configuré</p>
          </div>
        )}
      </div>

      {/* Informations sur les workflows */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6 border border-gray-200">
        <h4 className="font-semibold text-gray-800 mb-3">Exemples de workflows :</h4>
        <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
          <li>Parcours de chirurgie ambulatoire</li>
          <li>Parcours de consultation</li>
          <li>Parcours d'hospitalisation complète</li>
          <li>Parcours de téléconsultation</li>
          <li>Parcours urgences</li>
        </ul>
      </div>
    </div>
  );
};